import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const SelectList = ({ candidates, selected, max, onToggle }) => (
    <div className="max-h-56 overflow-y-auto border border-[#E5E7EB] rounded-lg divide-y" data-testid="batch-quota-select-list">
        {candidates.map((c) => {
            const checked = selected.includes(c.id);
            const disabled = !checked && selected.length >= max;
            return (
                <label key={c.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-[#F9FAFB]'}`}>
                    <Checkbox
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={() => onToggle(c.id)}
                        data-testid={`batch-quota-cb-${c.id}`}
                    />
                    <span className="truncate">{c.name || c.id}</span>
                </label>
            );
        })}
    </div>
);

export const BatchQuotaDialog = ({ open, job, preview, busy, notice, onClose, onLaunchSelected, onLaunchAll }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('choose');
    const [selected, setSelected] = useState([]);
    const [ack, setAck] = useState(false);
    const total = preview?.total || 0;
    const remaining = preview?.remaining_credits || 0;

    useEffect(() => {
        const ids = new Set((preview?.candidates || []).map((c) => c.id));
        setAck(false);
        setSelected((prev) => prev.filter((id) => ids.has(id)).slice(0, remaining));
    }, [preview, remaining]);
    useEffect(() => { if (!open) { setMode('choose'); setSelected([]); setAck(false); } }, [open]);

    const toggle = (id) => setSelected((prev) => (prev.includes(id)
        ? prev.filter((x) => x !== id)
        : (prev.length < remaining ? [...prev, id] : prev)));

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent className="sm:max-w-lg" data-testid="batch-quota-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-[#D97706]" />
                        {t('jobs.batch_quota_title')} — {job?.title}
                    </DialogTitle>
                    <DialogDescription data-testid="batch-quota-summary">
                        {t('jobs.batch_quota_summary', { pending: total, remaining })}
                    </DialogDescription>
                </DialogHeader>
                {notice && <p className="text-sm text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-2" data-testid="batch-quota-notice">{notice}</p>}
                {remaining === 0 ? (
                    <div className="space-y-4">
                        <p className="text-sm text-[#4B5563]" data-testid="batch-quota-zero">{t('jobs.batch_quota_zero')}</p>
                        <div className="flex justify-end"><Button variant="outline" onClick={onClose} data-testid="batch-quota-close">{t('common.close')}</Button></div>
                    </div>
                ) : mode === 'choose' ? (
                    <div className="space-y-4">
                        <p className="text-sm text-[#4B5563]">{t('jobs.batch_quota_explain', { can: remaining, rest: total - remaining })}</p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-end">
                            <Button variant="outline" onClick={onClose} data-testid="batch-quota-cancel">{t('common.cancel')}</Button>
                            <Button variant="outline" onClick={() => setMode('all')} data-testid="batch-quota-option-all">{t('jobs.batch_quota_option_all', { count: total })}</Button>
                            <Button className="bg-[#002FA7] hover:bg-[#002480] text-white" onClick={() => setMode('select')} data-testid="batch-quota-option-select">{t('jobs.batch_quota_option_select', { count: remaining })}</Button>
                        </div>
                    </div>
                ) : mode === 'select' ? (
                    <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                            <span data-testid="batch-quota-selected-count">{t('jobs.batch_quota_selected', { count: selected.length, max: remaining })}</span>
                            {selected.length >= remaining && <span className="text-[#B45309]" data-testid="batch-quota-limit-reached">{t('jobs.batch_quota_limit_reached')}</span>}
                        </div>
                        <SelectList candidates={preview?.candidates || []} selected={selected} max={remaining} onToggle={toggle} />
                        <p className="text-xs text-[#9CA3AF]">{t('jobs.batch_quota_unselected_info')}</p>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setMode('choose')} disabled={busy}>{t('common.back')}</Button>
                            <Button className="bg-[#002FA7] hover:bg-[#002480] text-white" disabled={busy || selected.length === 0} onClick={() => onLaunchSelected(selected)} data-testid="batch-quota-launch-selected">
                                {t('jobs.batch_quota_launch_selected', { count: selected.length })}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-md p-3" data-testid="batch-quota-all-warning">
                            {t('jobs.batch_quota_all_warning', { can: remaining, rest: total - remaining })}
                        </p>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={ack} onCheckedChange={(v) => setAck(v === true)} data-testid="batch-quota-ack" />
                            {t('jobs.batch_quota_ack')}
                        </label>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setMode('choose')} disabled={busy}>{t('common.back')}</Button>
                            <Button className="bg-[#D97706] hover:bg-[#B45309] text-white" disabled={busy || !ack} onClick={() => onLaunchAll()} data-testid="batch-quota-confirm-all">
                                {t('jobs.batch_quota_confirm_all', { count: total })}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
